import React from "react"
import { isNetworkRequestInFlight, type NetworkStatus } from "@apollo/client/core/networkStatus"
import {
  type Category,
  CategoryState,
  CategorySubtype,
  type CategoryType,
  type EntityCategory,
  ObjectKind,
  useListCategoriesQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import { defined } from "@digits-shared/helpers/filters"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import {
  type CategoryTypeaheadResult,
  TypeaheadResultDecorator,
} from "src/shared/components/Typeahead/sharedTypeahead"
import { convertCategoryTreeToFlatListAndSort } from "src/shared/helpers/hierarchicalCategoryHelper"
import {
  convertToHierarchicalDimensionTree,
  getHierarchicalDimensionAncestors,
  type HierarchicalDimensionNode,
} from "src/shared/helpers/hierarchicalDimensionHelper"

enum CategorySortPriority {
  NameExactMatch,
  NameStartsWithMatch,
  NameSubstringMatch,
  NumberExactMatch,
  NumberStartsWithMatch,
  NumberSubstringMatch,
  DescriptionMatch,
}

interface TypeaheadCategoriesProps {
  text: string
  includedTypes?: CategoryType[]
  includedSubtypes?: CategorySubtype[]
  includedStates?: CategoryState[]
  enableSelectRootCategory?: boolean
  enableSelectParentCategory?: boolean
  suggestedCategoryIds?: string[]
  excludedCategoryIds?: string[]
  excludedAncestorIds?: string[]
  includedCategoryIds?: string[]
  flatten?: boolean
}

export function useTypeaheadCategories({
  text,
  includedTypes,
  includedSubtypes,
  includedStates,
  enableSelectParentCategory,
  enableSelectRootCategory,
  suggestedCategoryIds,
  excludedCategoryIds,
  excludedAncestorIds,
  includedCategoryIds,
  flatten,
}: TypeaheadCategoriesProps) {
  const { result, networkStatus } = useCategoriesList()

  const { displayCategories, inactiveCategories, suggestedCategories } = React.useMemo(
    () =>
      buildCategoryResults({
        categories: result,
        includedTypes,
        includedSubtypes,
        includedStates,
        enableSelectParentCategory,
        enableSelectRootCategory,
        excludedCategoryIds,
        excludedAncestorIds,
        includedCategoryIds,
        suggestedCategoryIds,
        flatten,
        text,
      }),
    [
      enableSelectParentCategory,
      enableSelectRootCategory,
      suggestedCategoryIds,
      excludedCategoryIds,
      excludedAncestorIds,
      flatten,
      includedCategoryIds,
      includedStates,
      includedSubtypes,
      includedTypes,
      result,
      text,
    ]
  )

  const filterAndSort = React.useCallback(
    (categories: CategoryTypeaheadResult[]) => {
      const query = text.trim().toLowerCase()
      if (!query) return categories

      const sortPriority = (category: CategoryTypeaheadResult) => {
        switch (true) {
          case category.title.toLowerCase() === query:
            return CategorySortPriority.NameExactMatch
          case category.title.toLowerCase().startsWith(query):
            return CategorySortPriority.NameStartsWithMatch
          case category.title.toLowerCase().includes(query):
            return CategorySortPriority.NameSubstringMatch
          case category.displayNumber?.toLowerCase() === query:
            return CategorySortPriority.NumberExactMatch
          case category.displayNumber?.toLowerCase().startsWith(query):
            return CategorySortPriority.NumberStartsWithMatch
          case category.displayNumber?.toLowerCase().includes(query):
            return CategorySortPriority.NumberSubstringMatch
          case category.searchSubtitle?.toLowerCase().includes(query):
            return CategorySortPriority.DescriptionMatch
          default:
            return -1
        }
      }

      return categories
        .filter((c) => c.searchValues.join(" ").toLowerCase().includes(query))
        .sort((a, b) => {
          const aPriority = sortPriority(a)
          const bPriority = sortPriority(b)

          return aPriority === bPriority ? a.title.localeCompare(b.title) : aPriority - bPriority
        })
    },

    [text]
  )

  return React.useMemo(
    () => ({
      loading: isNetworkRequestInFlight(networkStatus),
      results: filterAndSort(displayCategories),
      suggestedResults: filterAndSort(suggestedCategories),
      newResults: filterAndSort(inactiveCategories),
    }),
    [networkStatus, filterAndSort, displayCategories, suggestedCategories, inactiveCategories]
  )
}

function useCategoriesList(): {
  result: Category[]
  networkStatus: NetworkStatus
} {
  const viewKey = useViewVersion()
  const response = useListCategoriesQuery({
    variables: { viewKey },
  })

  const { networkStatus } = response
  const result = response.loading ? response.previousData : response.data

  return {
    result: result?.listCategories || [],
    networkStatus,
  }
}

function buildAncestorMaps(flatTreeList: HierarchicalDimensionNode<Category>[]) {
  const categoryAncestors = new Map<string, EntityCategory[]>()
  const categoryIdToHierarchyText = new Map<string, string>()

  flatTreeList.forEach((node) => {
    const ancestorCategories = getHierarchicalDimensionAncestors(node).map((a) => a.dimension)
    const categoryId = node.dimension.id

    categoryAncestors.set(categoryId, ancestorCategories)
    categoryIdToHierarchyText.set(categoryId, ancestorCategories.map((anc) => anc.name).join(" ▸ "))
  })

  return { categoryAncestors, categoryIdToHierarchyText }
}

function categoryToTypeaheadResult({
  node,
  hierarchyText,
  enableSelectRootCategory = false,
  enableSelectParentCategory = false,
  flatten = false,
}: {
  node: HierarchicalDimensionNode<Category>
  hierarchyText: string | undefined
  enableSelectRootCategory?: boolean
  enableSelectParentCategory?: boolean
  flatten?: boolean
}): CategoryTypeaheadResult {
  const { dimension: category } = node
  const isRoot = !node.parent || node.dimension.subtype === CategorySubtype.None
  const isParent = node.children.length > 0

  return {
    id: category.id,
    title: category.name,
    depth: flatten ? 0 : (category.ancestors?.length ?? 0),
    displayNumber: category.displayNumber || undefined,
    subtitle: [category.displayNumber, hierarchyText].filter(defined).join(" - "),
    searchValues: [category.name, category.displayNumber, category.description].filter(defined),
    searchSubtitle: category.description,
    kind: ObjectKind.Category,
    entity: category,
    decorator:
      category.categoryState === CategoryState.CategoryDisabled
        ? TypeaheadResultDecorator.CategoryDisabled
        : category.categoryState === CategoryState.CategoryInactive
          ? TypeaheadResultDecorator.NewCategory
          : undefined,
    isSelectable:
      (enableSelectRootCategory || !isRoot) && (enableSelectParentCategory || !isParent),
    isNew: category.categoryState === CategoryState.CategoryInactive,
  }
}

function suggestedCategoryToTypeaheadResult(
  category: Category,
  hierarchyText: string | undefined
): CategoryTypeaheadResult {
  return {
    id: category.id,
    title: category.name,
    depth: 0,
    displayNumber: category.displayNumber || undefined,
    subtitle: [category.displayNumber, hierarchyText].filter(defined).join(" - "),
    searchValues: [category.name, category.displayNumber, category.description].filter(defined),
    searchSubtitle: category.description,
    kind: ObjectKind.Category,
    entity: category,
    isSelectable: true,
    isSuggestion: true,
    isNew: category.categoryState === CategoryState.CategoryInactive,
    decorator:
      category.categoryState === CategoryState.CategoryInactive
        ? TypeaheadResultDecorator.NewCategory
        : undefined,
  }
}

function buildCategoryResults({
  categories,
  includedTypes,
  includedSubtypes,
  includedStates,
  suggestedCategoryIds,
  enableSelectParentCategory,
  enableSelectRootCategory,
  excludedCategoryIds,
  excludedAncestorIds,
  includedCategoryIds,
  flatten,
}: { categories: Category[] } & TypeaheadCategoriesProps) {
  const categoryTree = convertToHierarchicalDimensionTree(categories, 0)
  const flatTreeList = convertCategoryTreeToFlatListAndSort(categoryTree)
  const { categoryIdToHierarchyText } = buildAncestorMaps(flatTreeList)

  const hasExcludedAncestors = excludedAncestorIds && excludedAncestorIds.length > 0

  const isIncludedType = (category: Category) =>
    !includedTypes || includedTypes.includes(category.type)

  const isIncludedSubtype = (category: Category) =>
    !includedSubtypes || includedSubtypes.includes(category.subtype)

  const isIncludedState = (category: Category) =>
    !includedStates ||
    includedStates.includes(category.categoryState) ||
    // Temporary while we figure out why ledger view categories are coming back unknown
    category.categoryState === CategoryState.UnknownCategoryState

  const isNotExcluded = (category: Category) => !excludedCategoryIds?.includes(category.id)

  const isNotExcludedAncestor = (category: Category) =>
    !hasExcludedAncestors ||
    (category.ancestors
      ? category.ancestors.every((a) => !excludedAncestorIds?.includes(a.id))
      : true)

  const isIncludedCategory = (category: Category) => includedCategoryIds?.includes(category.id)

  // Display categories are those that show up by default in the typeahead
  const displayCategories = flatTreeList
    .filter(
      ({ dimension: category }) =>
        isIncludedCategory(category) ||
        (isIncludedType(category) &&
          isIncludedSubtype(category) &&
          isIncludedState(category) &&
          isNotExcluded(category) &&
          isNotExcludedAncestor(category))
    )
    .map((node) =>
      categoryToTypeaheadResult({
        node,
        hierarchyText: categoryIdToHierarchyText.get(node.dimension.id),
        enableSelectParentCategory,
        enableSelectRootCategory,
        flatten,
      })
    )

  // Inactive categories are visible once the user starts typing in the typeahead and can be activated on the spot
  // To the user, we display them as new since they are otherwise not visible in their Chart of Accounts
  // Note: Inactive categories intentionally bypass the `includedStates` filter
  const inactiveCategories = flatTreeList
    .filter(
      ({ dimension: category }) =>
        category.categoryState === CategoryState.CategoryInactive &&
        isIncludedType(category) &&
        isIncludedSubtype(category) &&
        isNotExcluded(category) &&
        isNotExcludedAncestor(category)
    )
    .map((node) =>
      categoryToTypeaheadResult({
        node,
        hierarchyText: categoryIdToHierarchyText.get(node.dimension.id),
        enableSelectParentCategory,
        enableSelectRootCategory,
        flatten: true,
      })
    )

  const suggestedCategories = suggestedCategoryIds
    ? suggestedCategoryIds
        .map((id) => flatTreeList?.find(({ dimension: c }) => c.id === id))
        .filter(defined)
        .map(({ dimension: c }) =>
          suggestedCategoryToTypeaheadResult(c, categoryIdToHierarchyText.get(c.id))
        )
    : []

  return {
    displayCategories,
    inactiveCategories,
    suggestedCategories,
  }
}
